import { useState } from 'react';
import { API } from '../lib/site';
import { devWarn, devDebug } from '../lib/log';

const makeMsgId = () => `msg-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

const getSessionId = () => {
  let id = sessionStorage.getItem('melopa_chat_session');
  if (!id) {
    id = `chat-${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
    sessionStorage.setItem('melopa_chat_session', id);
  }
  return id;
};

const FALLBACK_ERROR = 'The assistant is unavailable right now. Please try again shortly.';
const CONNECTION_ERROR = 'Connection issue \u2014 please try again, or reach us via GET PROJECT ESTIMATE / WhatsApp.';

/**
 * Encapsulates the visitor chat SSE streaming logic.
 * Returns { messages, streaming, sendMessage }.
 */
export function useChatStream() {
  const [messages, setMessages] = useState([]);
  const [streaming, setStreaming] = useState(false);

  const replaceLast = (updater) =>
    setMessages((m) => {
      const copy = [...m];
      copy[copy.length - 1] = updater(copy[copy.length - 1]);
      return copy;
    });

  const appendDelta = (delta) => replaceLast((last) => ({ ...last, content: last.content + delta }));

  const processSseChunk = (chunkLines) => {
    for (const line of chunkLines) {
      const trimmed = line.trim();
      if (!trimmed.startsWith('data: ')) continue;
      try {
        const data = JSON.parse(trimmed.slice(6));
        if (data.delta) appendDelta(data.delta);
      } catch (frameErr) {
        // Incomplete SSE frame — remainder arrives with the next chunk.
        devDebug('Skipping partial SSE frame:', frameErr);
      }
    }
  };

  const readErrorDetail = async (res) => {
    try {
      const err = await res.json();
      if (typeof err.detail === 'string') return err.detail;
    } catch (parseErr) {
      devWarn('Chat error payload was not JSON:', parseErr);
    }
    return FALLBACK_ERROR;
  };

  const sendMessage = async (message) => {
    if (!message || streaming) return;
    setMessages((m) => [
      ...m,
      { id: makeMsgId(), role: 'user', content: message },
      { id: makeMsgId(), role: 'assistant', content: '' },
    ]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: getSessionId(), message }),
      });
      if (!res.ok || !res.body) {
        const detail = await readErrorDetail(res);
        replaceLast((last) => ({ ...last, content: detail }));
        return;
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let done = false;
      while (!done) {
        const chunk = await reader.read();
        done = chunk.done;
        if (chunk.value) {
          buffer += decoder.decode(chunk.value, { stream: true });
          const lines = buffer.split('\n\n');
          buffer = lines.pop() || '';
          processSseChunk(lines);
        }
      }
    } catch (e) {
      devWarn('Chat stream failed:', e);
      replaceLast((last) => ({ ...last, role: 'assistant', content: CONNECTION_ERROR }));
    } finally {
      setStreaming(false);
    }
  };

  return { messages, streaming, sendMessage };
}
